import { useEffect, useState, useMemo } from 'react'
import {
  useWebhooks,
  useWebhookDelete,
  useWebhookHelpers,
  useEditWebhookState,
} from './webhooks.hooks'
import { useVerifyPermission } from '@yuno/dashboard-container-mfe'
import { PermissionsAvailable } from '@yuno/dashboard-authentication-mfe'
import { Icon, Switch, Tag, TIcons } from '@yuno/design-system-web'
import emptyImg from '../../assets/empty_state.svg'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Table,
  TableCell,
  TableRow,
  TableEmpty,
  Dialog,
  Snackbar,
  Tooltip,
  useTheme,
  Theme,
} from '@yuno/design-system-web'

import PageTitle from '../../components/page-title'
import WebhookDrawer from './components/webhook-drawer'
import {
  StyledWrapper,
  SectionHeader,
  StyledTableCellUrl,
  StyledTableCellEvent,
  IconWrapper,
  DialogContainer,
  DialogText,
  SwitchWrapper,
  TableCellTagStyled,
  TagWrapper,
  TagsContainer,
  EmptyWrapper,
} from './style'
import { TableAction } from './components/common/table-action'
import { EWebhookStatus, TWebhook } from '../../typings/pages'
import { apiStatus } from '../../typings/pages/utils'
import { AlertColor } from '@mui/material'

const WebhooksContainer = () => {
  const theme: Theme = useTheme()
  const { allowed } = useVerifyPermission(PermissionsAvailable.MANAGE_WEBHOOKS)
  const [openDrawer, setOpenDrawer] = useState<boolean>(false)
  const [openEditDrawer, setOpenEditDrawer] = useState<boolean>(false)
  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false)
  const [codeToDelete, setCodeToDelete] = useState<string>('')
  const [nameToDelete, setNameToDelete] = useState<string>('')
  const [stateEnableText, setStateEnableText] = useState<string>('')
  const [snackbarParams, setSnackbarParams] = useState<{
    icon: TIcons
    severity: AlertColor
    message: string
  }>({
    icon: 'CheckCircle',
    severity: 'success',
    message: '',
  })
  const { t } = useTranslation()

  const COLUMNS = [
    { label: t('status') },
    { label: t('name') },
    { label: t('enpointUrl') },
    { label: t('triggers') },
  ]

  const [payload, setPayload] = useState<TWebhook | null>(null)

  const { webhooks, isLoading } = useWebhooks()
  const { handleChangeState, status } = useEditWebhookState()
  const {
    helpers: { transformStatus, getTotalActions },
  } = useWebhookHelpers()
  const {
    handleDelete,
    isLoading: isDeletion,
    statusDelete,
  } = useWebhookDelete({
    onSuccess: handleOnSuccessDeletion,
  })

  useEffect(() => {
    if (status === apiStatus.idle || status === apiStatus.loading) return
    const isSuccess = status === apiStatus.success
    let message =
      stateEnableText === 'disabled'
        ? t('couldntDisableWebhook')
        : t('couldntEnableWebhook')
    if (isSuccess) {
      message =
        stateEnableText === 'disabled'
          ? t('webhookDisabled')
          : t('webhookEnabled')
    }
    setSnackbarParams({
      icon: isSuccess ? 'CheckCircle' : 'Info',
      severity: isSuccess ? 'success' : 'error',
      message,
    })

    setOpenSnackbar(true)
  }, [status])

  useEffect(() => {
    if (statusDelete === apiStatus.idle || statusDelete === apiStatus.loading)
      return
    const isSuccess = statusDelete === apiStatus.success
    let message = t('couldntDeleteWebhook')
    if (isSuccess) {
      message = t('webhookSuccessfullyDeleted')
    }
    setSnackbarParams({
      icon: isSuccess ? 'CheckCircle' : 'Info',
      severity: isSuccess ? 'success' : 'error',
      message,
    })
    setOpenSnackbar(true)
    handleOpenDialog()
  }, [statusDelete])

  function handleOpenDrawer() {
    setOpenDrawer((prev) => !prev)
  }

  function handleOpenEditDrawer() {
    setOpenEditDrawer((prev) => !prev)
  }

  function handleOpenDialog() {
    setOpenDialog((prev) => !prev)
  }

  function handleOnCreate() {
    setPayload(null)
    handleOpenDrawer()
  }

  function handleOnEdit(row: any) {
    setPayload(row)
    handleOpenEditDrawer()
  }

  function handleOnDelete(code: any, name: string) {
    setCodeToDelete(code)
    setNameToDelete(name)
    handleOpenDialog()
  }

  function handleOnSuccessDeletion() {
    setCodeToDelete('')
    setNameToDelete('')
  }

  function handleOnSuccessEdition() {
    handleOpenEditDrawer()
    setSnackbarParams({
      icon: 'CheckCircle',
      severity: 'success',
      message: t('weebHookEdited'),
    })
    setOpenSnackbar(true)
  }

  function handleOnErrorEdition() {
    handleOpenEditDrawer()
    setSnackbarParams({
      icon: 'Info',
      severity: 'error',
      message: t('couldntEdit'),
    })
    setOpenSnackbar(true)
  }

  const handleOnEditStatus = (id: number, stateWebhook: string) => {
    const state =
      stateWebhook === EWebhookStatus.active
        ? { name: EWebhookStatus.inactive, text: 'disabled' }
        : { name: EWebhookStatus.active, text: 'enabled' }
    setStateEnableText(state.text)
    handleChangeState(id, state.name)
  }

  const buttons = useMemo(() => {
    if (!Boolean(codeToDelete)) return []

    return [
      {
        variant: 'tertiary',
        label: t('cancel'),
        size: 'small',
        event: handleOpenDialog,
        disabled: isDeletion,
      },
      {
        variant: 'primary',
        label: t('yesDelete'),
        size: 'small',
        event: () => handleDelete(codeToDelete),
        disabled: isDeletion,
      },
    ]
  }, [codeToDelete, handleDelete, handleOpenDialog, isDeletion])

  return (
    <>
      {openEditDrawer && (
        <WebhookDrawer
          webhooksList={webhooks}
          open={openEditDrawer}
          onClose={handleOpenEditDrawer}
          onSuccess={handleOnSuccessEdition}
          payload={payload}
          onError={handleOnErrorEdition}
        />
      )}
      {openDrawer && (
        <WebhookDrawer
          webhooksList={webhooks}
          open={openDrawer}
          onClose={handleOpenDrawer}
          payload={payload}
          onSuccess={null}
        />
      )}
      {!isLoading && webhooks.length === 0 ? (
        <EmptyWrapper>
          <TableEmpty
            icon={emptyImg}
            title={t('noWebhooks')}
            subtitle={t('noWebhooksAddHere')}
          >
            <Tooltip
              disableHoverListener={allowed}
              title={t('noAccess')}
              color="default"
              arrow
            >
              <span>
                <Button
                  disabled={!allowed}
                  size="small"
                  onClick={handleOnCreate}
                >
                  {t('addWebhook')}
                </Button>
              </span>
            </Tooltip>
          </TableEmpty>
        </EmptyWrapper>
      ) : (
        <StyledWrapper>
          <SectionHeader>
            <PageTitle>{t('webhooks')}</PageTitle>
            {!isLoading && (
              <Tooltip
                disableHoverListener={allowed}
                title={t('noAccess')}
                color="default"
                arrow
              >
                <span>
                  <Button
                    size="small"
                    onClick={handleOnCreate}
                    disabled={!allowed}
                  >
                    {t('addWebhook')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </SectionHeader>
          <Table
            ref={null}
            columns={COLUMNS}
            actionsColumn={true}
            loading={isLoading}
            showEmptyTable={false}
          >
            {!isLoading &&
              webhooks.map((row, index) => {
                const actions = getTotalActions(row)
                return (
                  <TableRow
                    sx={{ width: '100%' }}
                    hover
                    tabIndex={-1}
                    key={index}
                  >
                    <StyledTableCellEvent>
                      <Tooltip
                        disableHoverListener={allowed}
                        title={t('noAccess')}
                        color="default"
                        arrow
                      >
                        <SwitchWrapper>
                          <Switch
                            disabled={!allowed}
                            checked={transformStatus(row.state)}
                            onChange={() =>
                              handleOnEditStatus(row.id, row.state)
                            }
                          />
                        </SwitchWrapper>
                      </Tooltip>
                    </StyledTableCellEvent>
                    <StyledTableCellEvent>{row.name}</StyledTableCellEvent>
                    <StyledTableCellUrl>{row.url}</StyledTableCellUrl>
                    <TableCellTagStyled>
                      <TagsContainer>
                        {actions?.actions.map((action, index) => (
                          <TagWrapper>
                            <Tag key={index} type="default" label={action} />
                          </TagWrapper>
                        ))}

                        {actions.actionsTooltip.length > 0 && (
                          <Tooltip
                            title={actions.tooltipLabel}
                            color="default"
                            arrow
                          >
                            <TagWrapper>
                              <Tag
                                key={index}
                                type="default"
                                label={`+${actions.actionsTooltip.length.toString()}`}
                              />
                            </TagWrapper>
                          </Tooltip>
                        )}
                      </TagsContainer>
                    </TableCellTagStyled>
                    <TableCell>
                      <IconWrapper>
                        <TableAction
                          onClick={() => handleOnEdit(row)}
                          icon="PencilSimpleLine"
                          allowed={allowed}
                          hoverColor={theme.palette.vivid.brand}
                        />
                        <TableAction
                          onClick={() => handleOnDelete(row.id, row.name)}
                          icon="Trash"
                          allowed={allowed}
                          hoverColor={theme.palette.vivid.coral}
                        />
                      </IconWrapper>
                    </TableCell>
                  </TableRow>
                )
              })}
          </Table>
          {openDialog && (
            <Dialog
              allowed={false}
              title={`${t('delete')} ${nameToDelete}`}
              open={openDialog}
              closeIcon={handleOpenDialog}
              buttons={buttons as never}
            >
              <DialogContainer>
                <DialogText>{t('confirmDelete')}</DialogText>
                <DialogText>{t('confirmDeleteFinal')}</DialogText>
              </DialogContainer>
            </Dialog>
          )}
        </StyledWrapper>
      )}
      {openSnackbar && (
        <Snackbar
          icon={snackbarParams.icon}
          severity={snackbarParams.severity}
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={() => setOpenSnackbar(false)}
          message={snackbarParams.message}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          width="380px"
        />
      )}
    </>
  )
}

export default WebhooksContainer
